import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, ErrorHandler, Injector, NgModule, provideZonelessChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FlatpickrModule } from 'angularx-flatpickr';

import { GlobalErrorComponent } from 'lib/error/global-error.component.js';
import { GlobalErrorHandler } from 'lib/error/global-error.handler.js';
import { ImagesModule } from 'lib/images/images.module.js';
import { AzureSearchModule } from 'lib/search/client/azure-search.module.js';
import { FormModule } from 'lib/ui/form/form.module.js';
import { HoverModule } from 'lib/ui/hover/index.js';
import { CoreModule } from 'src/app/core/core.module.js';
import { AppRoutingModule } from './app-routing.module.js';
import { environment } from './environments/environment.js';
import { LayoutModule } from './layout/layout.module.js';
import { AnalyticsModule } from './modules/analytics/analytics.module.js';
import { AppSetupModule } from './modules/app-setup/app-setup.module.js';
import { BackgroundLogsModule } from './modules/background-logs/background-logs.module.js';
import { BrandingModule } from './modules/branding/branding.module.js';
import { CalendarModule } from './modules/calendar/calendar.module.js';
import { ChatModule } from './modules/chat/chat.module.js';
import { ContactModule } from './modules/contact/contact.module.js';
import { DietsModule } from './modules/diets/diets.module.js';
import { ExercisesModule } from './modules/exercises/exercises.module.js';
import { FoodsModule } from './modules/foods/foods.module.js';
import { HomeModule } from './modules/home/home.module.js';
import { ImageGalleryModule } from './modules/image-gallery/image-gallery.module.js';
import { InstallModule } from './modules/install/install.module.js';
import { LogsModule } from './modules/logs/logs.module.js';
import { PackagesModule } from './modules/packages/packages.module.js';
import { ProgressModule } from './modules/progress/progress.module.js';
import { SharedComponentsModule } from './modules/shared/sharedComponents.module.js';
import { SubscriptionModule } from './modules/subscription/subscription.module.js';
import { TasksModule } from './modules/tasks/tasks.module.js';
import { UsersModule } from './modules/users/users.module.js';
import { WorkoutsModule } from './modules/workouts/workouts.module.js';
import { AboutModule } from './pages/about/about.module.js';
import { AuthModule } from './pages/auth/auth.module.js';
import { EmptyComponent } from './pages/home/empty.component.js';
import { HomeComponent } from './pages/home/home.component.js';
import { PdfModule } from './pages/pdf/pdf.module.js';
import { PermissionsModule } from './pages/permissions/permissions.module.js';
import { TypeRoutesModule } from './pages/type-routes/type-routes.module.js';
import { RootComponent } from './root.component.js';
import { adminAnalyticsRoutes } from './routes/admin/admin-analytics-routes.js';
import { adminAppSetupRoutes } from './routes/admin/admin-app-setup-routes.js';
import { adminImageGalleryRoutes } from './routes/admin/admin-image-gallery-routes.js';
import { adminLogRoutes } from './routes/admin/admin-log-routes.js';
import { adminUserRoutes } from './routes/admin/admin-user-routes.js';
import { clientRoutes } from './routes/client/client-routes.js';
import { brandingRoutes } from './routes/profile/branding-routes.js';
import { calendarRoutes } from './routes/profile/calendar-routes.js';
import { chatRoutes } from './routes/profile/chat-routes.js';
import { helpdeskRoutes } from './routes/profile/helpdesk-routes.js';
import { profileRoutes } from './routes/profile/profile-routes.js';
import { dietRoutes } from './routes/trainer/diet-routes.js';
import { exerciseRoutes } from './routes/trainer/exercise-routes.js';
import { foodRoutes } from './routes/trainer/food-routes.js';
import { genaiRoutes } from './routes/trainer/genai-routes.js';
import { packageRoutes } from './routes/trainer/package-routes.js';
import { progressRoutes } from './routes/trainer/progress-routes.js';
import { taskRoutes } from './routes/trainer/task-routes.js';
import { trainerClientRoutes } from './routes/trainer/trainer-client-routes.js';
import { trainerHomeRoutes } from './routes/trainer/trainer-home-routes.js';
import { workoutRoutes } from './routes/trainer/workout-routes.js';

@NgModule({
	declarations: [RootComponent, HomeComponent, EmptyComponent, GlobalErrorComponent],
	bootstrap: [RootComponent],
	imports: [
		CoreModule,
		BrowserModule,
		AppRoutingModule,
		LayoutModule,
		ImagesModule,
		HoverModule,
		// Type routes
		TypeRoutesModule.forRoot({
			appRoutes: {
				chatRoutes,
				trainerClientRoutes,
				workoutRoutes,
				profileRoutes,
				exerciseRoutes,
				foodRoutes,
				dietRoutes,
				progressRoutes,
				taskRoutes,
				brandingRoutes,
				packageRoutes,
				trainerHomeRoutes,
				calendarRoutes,
				adminLogRoutes,
				adminUserRoutes,
				clientRoutes,
				adminAppSetupRoutes,
				adminAnalyticsRoutes,
				helpdeskRoutes,
				adminImageGalleryRoutes,
				genaiRoutes
			}
		}),
		// Basic modules
		AuthModule,
		// External feature modules
		FlatpickrModule.forRoot(),
		// Pdf module
		PdfModule,
		// Feature modules
		AppSetupModule,
		ImageGalleryModule,
		AnalyticsModule,
		AboutModule,
		HomeModule,
		SharedComponentsModule,
		FormModule,
		ChatModule,
		UsersModule,
		WorkoutsModule,
		ExercisesModule,
		FoodsModule,
		DietsModule,
		ProgressModule,
		TasksModule,
		BrandingModule,
		PackagesModule,
		CalendarModule,
		LogsModule,
		BackgroundLogsModule,
		SubscriptionModule,
		InstallModule,
		PermissionsModule,
		ContactModule,
		// Azure search
		AzureSearchModule.forRoot({
			endpoint: environment.azureSearch.endpoint,
			queryKey: environment.azureSearch.queryKey,
			isProduction: environment.production
		})
	],
	providers: [
		provideZonelessChangeDetection(),
		{ provide: APP_ID, useValue: 'serverApp' },
		provideHttpClient(withFetch(), withInterceptorsFromDi()),
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler,
			deps: [Injector]
		}
	]
})
export class AppModule {}
